// --------------------------------------------------------------
// Created On: 2025-05-05
// Author: Hannah Vaughan
//
// Last Modified: 2026-06-26
// Modified By: Jonathon Hicke
//
// Copyright 2025 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useMemo } from "react";
import styles from "./ModbusPage.module.scss";
import IconTooltip from "src/components/IconTooltip/IconTooltip";
import Warning from "src/components/Warning/Warning";

// Page of the Asset Management Wizard that allows the user to optionally select modbus controllers and/or sensors.
export default function ModbusPage(props: Props): Component {
  const modbusSensorInUseCount = useMemo(
    () => getModbusSensorInUseCount(props.modbusInfoInUse),
    [JSON.stringify(props.modbusInfoInUse)]
  );
  const sensorPositions = useMemo(
    () =>
      getModbusSensorPositionArray(
        props.modbusSensors,
        props.modbusInfoInUse,
        props.modbusSensorId,
        modbusSensorInUseCount
      ),
    [
      props.modbusSensorId,
      JSON.stringify(props.modbusInfoInUse),
      JSON.stringify(props.modbusSensors),
      modbusSensorInUseCount,
    ]
  );
  const [allModbusSensorsInUse, warningMessage] = useMemo(
    () =>
      getModbusSensorsInUseAndWarningMessage(
        props.controllers,
        props.controllerInUse,
        props.modbusInfoInUse,
        modbusSensorInUseCount
      ),
    [
      modbusSensorInUseCount,
      JSON.stringify(sensorPositions),
      JSON.stringify(props.controllers),
      JSON.stringify(props.controllerInUse),
      JSON.stringify(props.modbusInfoInUse),
    ]
  );

  // Set the modbus sensor if selecting an open position in a network where
  // another asset has already selected a modbus sensor type.
  // Note: all assets using modbus sensors in a network are required to use the same sensor type.
  useEffect(() => {
    if (props.modbusMedium === "Sensor" && props.modbusInfoInUse !== null && props.modbusInfoInUse !== undefined) {
      props.onChangeModbusSensorIndex(props.modbusInfoInUse.modbusSensorId ?? -1);
    }
  }, [props.modbusMedium, JSON.stringify(props.modbusInfoInUse)]);

  // Gets the modbus sensor count based on the sensor type selected.
  function getModbusSensorInUseCount(modbusInfoInUse: ModbusInfo | null): number {
    let modbusSensorCount = 0;
    if (modbusInfoInUse !== null && modbusInfoInUse.sensor !== null) {
      modbusSensorCount = Object.keys(modbusInfoInUse.sensor).length;
    }
    return modbusSensorCount;
  }

  // Displays an error message whenever a controller or sensor that is already in use
  // by the network is selected by the current asset.
  function getModbusSensorsInUseAndWarningMessage(
    controllers: Controller[],
    controllerInUse: ControllerInfo | null,
    modbusInfoInUse: ModbusInfo | null,
    modbusSensorInUseCount: number
  ): [boolean, string] {
    let allModbusSensorsInUse = false;
    let warningMessage = "";
    let assetNameInUse = "";

    // Get a flag for whether modbus sensors are in use in a network, a count for how many modbus sensors are in use in
    // a network, and a count for how many modbus sensor positions are available based on the selected modbus sensor type.
    const modbusSensorIsInUse = getModbusSensorInUse(modbusInfoInUse, modbusSensorInUseCount);
    const modbusSensorCount = sensorPositions.length;

    if (controllerInUse !== null) {
      // Display a warning if there are any assets in the network that already use a modbus controller.
      assetNameInUse = controllerInUse.assetName;
      const currentControllerInUse = controllers.find(
        (controller) => controller.controllerId === controllerInUse?.controllerId
      );
      let controllerNameInUse = "";
      if (currentControllerInUse !== undefined) {
        controllerNameInUse = currentControllerInUse.name;
      }
      allModbusSensorsInUse = false;
      warningMessage =
        `Asset '${assetNameInUse}' is already using the controller '${controllerNameInUse}' in this network. ` +
        "No other modbus devices are allowed.";
    } else if (modbusSensorIsInUse && modbusSensorInUseCount === modbusSensorCount) {
      // Display a warning if all sensor positions for the selected modbus sensor type are in use in the asset network.
      allModbusSensorsInUse = true;
      warningMessage = "All modbus sensors are already in use.";
    } else {
      allModbusSensorsInUse = false;
      warningMessage = "";
    }

    return [allModbusSensorsInUse, warningMessage];
  }

  // Checks whether there are any modbus sensors in use by other assets in the network.
  function getModbusSensorInUse(modbusInfoInUse: ModbusInfo | null, modbusSensorInUseCount: number): boolean {
    return (
      modbusInfoInUse !== null &&
      modbusInfoInUse !== undefined &&
      modbusInfoInUse.sensor !== null &&
      modbusInfoInUse.sensor !== undefined &&
      modbusSensorInUseCount > 0
    );
  }

  // Gets the array of all modbus sensor position numbers based on the max count allowed for the selected sensor.
  function getModbusSensorPositionArray(
    modbusSensors: ModbusSensor[],
    modbusInfoInUse: ModbusInfo | null,
    modbusSensorId: number,
    modbusSensorInUseCount: number
  ): number[] {
    let modbusSensor: ModbusSensor | undefined;
    if (modbusSensors !== null && modbusSensors !== undefined) {
      if (modbusSensorId > 0) {
        modbusSensor = modbusSensors.find((sensor) => sensor.modbusSensorId === modbusSensorId);
      } else if (getModbusSensorInUse(modbusInfoInUse, modbusSensorInUseCount)) {
        modbusSensor = modbusSensors.find((sensor) => sensor.modbusSensorId === modbusInfoInUse?.modbusSensorId);
      }
    }

    // Fill an array of 1 to X, with X being the max sensor count for the selected modbus sensor type.
    if (modbusSensor !== null && modbusSensor !== undefined) {
      return new Array<number>(modbusSensor.maxCount).fill(0).map((_, i) => i + 1);
    } else {
      return [];
    }
  }

  // Returns whether or not the provided sensor position is in use by another asset in the network.
  function getModbusSensorPositionInUseStatus(
    sensorPosition: number,
    modbusInfoInUse: ModbusInfo | null,
    modbusSensorInUseCount: number
  ): boolean {
    const modbusSensorsInUse = getModbusSensorInUse(modbusInfoInUse, modbusSensorInUseCount);
    let currentModbusSensorInUse = false;
    if (modbusInfoInUse !== null && modbusInfoInUse.sensor !== null) {
      currentModbusSensorInUse = Object.keys(modbusInfoInUse.sensor).includes(String(sensorPosition));
    }
    return modbusSensorsInUse && currentModbusSensorInUse;
  }

  return (
    <Fragment>
      <div className="col mx-3">
        <label className={`${styles.title} mb-3`}>How does this asset use Modbus?&nbsp;</label>
        <IconTooltip
          id="modbus-use-tooltip"
          icon="info-circle"
          message="The way in which the asset uses Modbus and collects Modbus data."
          color="var(--info-tooltip)"
        />
      </div>
      <div className="col mx-3">
        <div className="form-check form-check-inline">
          <input
            data-test="modbus-radio-select-not-used"
            className="form-check-input"
            type="radio"
            name="modbusControllerRadioOptions"
            id="modbusControllerRadioNotUsed"
            value="Not used"
            onChange={(e) => props.onChangeModbusMedium(e.target.value as ModbusMedium)}
            checked={props.modbusMedium === "Not used"}
            disabled={
              props.disabled ||
              (props.controllerInUse !== null && props.controllerInUse !== undefined) ||
              (getModbusSensorInUse(props.modbusInfoInUse, modbusSensorInUseCount) &&
                modbusSensorInUseCount === sensorPositions.length)
            }
          />
          <label className="form-check-label" htmlFor="modbusControllerRadio">
            Not used
          </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            data-test="modbus-radio-select-controller"
            className="form-check-input"
            type="radio"
            name="modbusControllerRadioOptions"
            id="modbusControllerRadioController"
            value="Controller"
            onChange={(e) => props.onChangeModbusMedium(e.target.value as ModbusMedium)}
            checked={props.modbusMedium === "Controller"}
            disabled={
              props.disabled ||
              (props.controllerInUse !== null && props.controllerInUse !== undefined) ||
              (props.modbusInfoInUse !== null && props.modbusInfoInUse !== undefined)
            }
          />
          <label className="form-check-label" htmlFor="modbusControllerRadioController">
            Controller
          </label>
        </div>
        <div className="form-check form-check-inline">
          <input
            data-test="modbus-radio-select-sensor"
            className="form-check-input"
            type="radio"
            name="modbusControllerRadioOptions"
            id="modbusControllerRadioSensor"
            value="Sensor"
            onChange={(e) => props.onChangeModbusMedium(e.target.value as ModbusMedium)}
            checked={props.modbusMedium === "Sensor"}
            disabled={props.disabled || props.controllerInUse !== null || allModbusSensorsInUse}
          />
          <label className="form-check-label" htmlFor="modbusControllerRadioSensor">
            Sensor(s)
          </label>
        </div>
      </div>
      {props.modbusMedium === "Controller" && (
        <div className="col m-3">
          <label className={`${styles.title} mb-3`}>Controller&nbsp;</label>
          <IconTooltip
            id="controller-tooltip"
            icon="info-circle"
            message={
              "The type of controller that is attached to the product." +
              " In most cases a controller will support starting and stopping the product."
            }
            color="var(--info-tooltip)"
          />
          <select
            data-test="controller-selection"
            className="form-select"
            value={props.controllerIndex}
            disabled={props.disabled}
            onChange={(e) => props.onChangeControllerIndex(parseInt(e.target.value, 10))}
          >
            <option value="-1">Please select a controller...</option>
            {props.controllers.map((controller, i) => (
              <option key={i} value={controller.controllerId}>
                {controller.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {props.modbusMedium === "Sensor" && (
        <Fragment>
          <div className="col m-3">
            <label className={`${styles.title} mb-3`}>Type of Sensor&nbsp;</label>
            {props.modbusInfoInUse === null || props.modbusInfoInUse === undefined ? (
              <select
                data-test="modbus-sensor-type-selection"
                className="form-select"
                value={props.modbusSensorId}
                onChange={(e) => {
                  const newModbusSensorIndex = parseInt(e.target.value, 10);
                  props.onChangeModbusSensorIndex(newModbusSensorIndex);
                }}
                disabled={props.disabled || props.modbusInfoInUse !== null}
              >
                <option value="-1">Please select a Modbus sensor type...</option>
                {props.modbusSensors !== undefined &&
                  props.modbusSensors.length > 0 &&
                  props.modbusSensors.map((sensor) => (
                    <option
                      key={sensor.modbusSensorId}
                      value={sensor.modbusSensorId}
                      selected={
                        props.modbusInfoInUse !== null && props.modbusInfoInUse.modbusSensorId === sensor.modbusSensorId
                      }
                    >
                      {sensor.name}
                    </option>
                  ))}
              </select>
            ) : (
              <select
                data-test="modbus-sensor-type-selection"
                className="form-select"
                value={props.modbusInfoInUse.modbusSensorId ?? -1}
                disabled
              >
                <option value={props.modbusInfoInUse.modbusSensorId ?? -1}>
                  {
                    props.modbusSensors.find(
                      (sensor) => sensor.modbusSensorId === props.modbusInfoInUse?.modbusSensorId
                    )?.name
                  }
                </option>
              </select>
            )}
          </div>
          <div className="col m-3">
            <label className={`${styles.title} mb-3`}>Sensor Position&nbsp;</label>
            <select
              data-test="modbus-sensor-position-selection"
              className="form-select"
              value={props.modbusSensorPosition}
              onChange={(e) => props.onChangeModbusSensorPosition(parseInt(e.target.value))}
              disabled={props.disabled}
            >
              <option value="-1">Please select a Modbus sensor position...</option>
              {sensorPositions.map((e, i) => (
                <option
                  key={i}
                  value={i + 1}
                  disabled={getModbusSensorPositionInUseStatus(i + 1, props.modbusInfoInUse, modbusSensorInUseCount)}
                >
                  {props.modbusInfoInUse !== null &&
                  props.modbusInfoInUse !== undefined &&
                  props.modbusInfoInUse.sensor !== null &&
                  props.modbusInfoInUse.sensor !== undefined &&
                  getModbusSensorPositionInUseStatus(i + 1, props.modbusInfoInUse, modbusSensorInUseCount)
                    ? `#${i + 1} - In use by '${props.modbusInfoInUse?.sensor[i + 1].assetName}'`
                    : `#${i + 1}`}
                </option>
              ))}
            </select>
          </div>
        </Fragment>
      )}
      {warningMessage.length > 0 && (
        <div className="col m-3">
          <Warning message={warningMessage} />
        </div>
      )}
    </Fragment>
  );
}

interface Props {
  modbusMedium: ModbusMedium;
  controllerIndex: number;
  controllers: Controller[];
  modbusSensorId: number;
  modbusSensors: ModbusSensor[];
  modbusSensorPosition: number;
  modbusInfoInUse: ModbusInfo | null;
  controllerInUse: ControllerInfo | null;
  assetName: string;
  disabled: boolean;
  onChangeModbusMedium: (modbusMedium: ModbusMedium) => void;
  onChangeControllerIndex: (controllerIndex: number) => void;
  onChangeModbusSensorIndex: (modbusSensorIndex: number) => void;
  onChangeModbusSensorPosition: (modbusSensorPosition: number) => void;
}

interface Controller {
  controllerId: number;
  controllerModel: string | null;
  manufacturerCompanyCode: string | null;
  name: string;
}

interface ModbusSensor {
  modbusSensorId: number;
  name: string;
  maxCount: number;
}

interface ControllerInfo {
  controllerId: number;
  assetName: string;
}

interface ModbusInfo {
  modbusSensorId: number | null;
  sensor: ModbusSensorInfoMap | null;
}

interface ModbusSensorInfoMap {
  [sensorNumber: number]: ModbusSensorInfo;
}

interface ModbusSensorInfo {
  assetName: string;
}

type ModbusMedium = "Not used" | "Controller" | "Sensor";
